import { useEffect, useCallback } from 'react'
import { useRouteMatch, useParams } from 'react-router'
import copy from 'copy-to-clipboard'
import { usePRFileSections } from '@sketch-hq/sketch-web-renderer'

import { keyWithModifierAndShift, shortcuts } from '@sketch/utils'
import { useToast } from '@sketch/toasts'
import {
  isDocumentPageRouteCanvas,
  isDocumentRoute,
  isFrameGroupRoute,
  isPageSectionRoute,
  routes,
} from '@sketch/modules-common'

import { useVersioning } from '../../versioning'
import {
  PreviewContextProps,
  usePreview,
} from '../../shares/views/PreviewView/PreviewContext'
import { useSelectedFrame } from '../components/SelectedFrameContext'

type LinkType = 'frame' | 'page' | 'section'

interface CopyLinkResult {
  url: string
  type: LinkType
}

/**
 * Sets up a global keyboard shortcut (Cmd+Shift+C on Mac, Ctrl+Shift+C on Windows)
 * that copies the current URL based on the view context:
 *
 * 1. When in a frame or Section focus view: copies that entity's URL
 * 2. When in page view with a frame or Section selected: copies its URL
 * 3. When viewing a page (nothing selected): copies the page URL
 * 4. Inside a Preview: the same, through the preview routes
 */
export function useCopyLinkShortcut(): void {
  const { showToast } = useToast()
  const { path } = useRouteMatch()
  const params = useParams<Record<string, string>>()
  const versioning = useVersioning()
  const preview = usePreview()
  const selectedFrameContext = useSelectedFrame()
  const sections = usePRFileSections()

  const createUrl = useCallback((): CopyLinkResult | null => {
    // Frame view - copy frame URL
    if (isFrameGroupRoute(path)) {
      const url = createFrameUrl(versioning, params.frameUUID)
      return url ? { url, type: 'frame' } : null
    }

    // Section focus view - copy the focused Section's URL
    if (isPageSectionRoute(path)) {
      const url = createSectionUrl(versioning, path, params, params.sectionUUID)
      return url ? { url, type: 'section' } : null
    }

    // Page canvas view with a top-level entity selected - copy its URL.
    // View-mode selection stores the selected entity's id in the frame slot
    // regardless of its kind, so resolve whether it is actually a Section and
    // build the matching URL.
    const isPageCanvasRoute =
      isDocumentPageRouteCanvas(path) || isDocumentRoute(path)
    if (isPageCanvasRoute && selectedFrameContext?.selectedFrameUUID) {
      const selectedUUID = selectedFrameContext.selectedFrameUUID
      const isSectionSelected = (sections ?? []).some(
        section => section.objectId === selectedUUID
      )

      if (isSectionSelected) {
        const url = createSectionUrl(versioning, path, params, selectedUUID)
        return url ? { url, type: 'section' } : null
      }

      const url = createFrameUrl(versioning, selectedUUID)
      return url ? { url, type: 'frame' } : null
    }

    // Page canvas view - copy page URL
    if (isPageCanvasRoute) {
      const url = createPageCanvasUrl(path, params, versioning)
      return url ? { url, type: 'page' } : null
    }

    // Preview views - copy the focused entity's URL, the selected entity's URL,
    // or the page URL
    return createPreviewResult(
      path,
      params,
      preview,
      selectedFrameContext?.selectedFrameUUID,
      sections
    )
  }, [
    path,
    params,
    versioning,
    preview,
    selectedFrameContext?.selectedFrameUUID,
    sections,
  ])

  useEffect((): (() => void) => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (!keyWithModifierAndShift(shortcuts.copyLink)(event)) {
        return
      }

      event.preventDefault()

      const result: CopyLinkResult | null = createUrl()
      if (result) {
        copy(`${window.location.origin}${result.url}`)
        showToast(
          result.type === 'frame'
            ? 'Copied link to frame'
            : result.type === 'section'
              ? 'Copied link to section'
              : 'Copied link to page'
        )
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [createUrl, showToast])
}

type Versioning = ReturnType<typeof useVersioning>

function createFrameUrl(
  versioning: Versioning,
  frameUUID: string | undefined
): string | null {
  if (versioning.loading || !frameUUID) {
    return null
  }

  return routes.FRAME.create({
    shareID: versioning.share.identifier,
    frameUUID,
  })
}

function createSectionUrl(
  versioning: Versioning,
  path: string,
  params: Record<string, string>,
  sectionUUID: string | undefined
): string | null {
  if (versioning.loading || !sectionUUID) {
    return null
  }

  // Both routes that can be current here carry the pageUUID; the document route
  // doesn't, and on it the default (first) page is the one on screen.
  const pageUUID =
    isDocumentPageRouteCanvas(path) || isPageSectionRoute(path)
      ? params.pageUUID
      : versioning.currentVersion?.document?.pages?.entries[0]?.uuid

  if (!pageUUID) {
    return null
  }

  return routes.PAGE_SECTION.create({
    shareID: versioning.share.identifier,
    pageUUID,
    sectionUUID,
  })
}

function createPageCanvasUrl(
  path: string,
  params: Record<string, string>,
  versioning: Versioning
): string | null {
  if (versioning.loading) {
    return null
  }

  const pageUUID = isDocumentPageRouteCanvas(path)
    ? params.pageUUID
    : versioning.currentVersion?.document?.pages?.entries[0]?.uuid

  if (!pageUUID) {
    return null
  }

  return routes.SHARE_PAGE_CANVAS_VIEW.create({
    shareID: versioning.share.identifier,
    pageUUID,
  })
}

/**
 * The link to copy while inside a Preview:
 * - in a focus view, the entity being focused (the current URL);
 * - on the page canvas with a top-level entity selected, that entity;
 * - otherwise the page itself.
 */
function createPreviewResult(
  path: string,
  params: Record<string, string>,
  preview: PreviewContextProps | undefined,
  selectedUUID: string | null | undefined,
  sections: ReturnType<typeof usePRFileSections>
): CopyLinkResult | null {
  if (!preview) {
    return null
  }

  if (path === routes.PREVIEW_FRAME_VIEW.template()) {
    return {
      url: routes.PREVIEW_FRAME_VIEW.create({
        previewID: params.previewID,
        pageUUID: params.pageUUID,
        frameUUID: params.frameUUID,
      }),
      type: 'frame',
    }
  }

  if (path === routes.PREVIEW_PAGE_SECTION.template()) {
    return {
      url: routes.PREVIEW_PAGE_SECTION.create({
        previewID: params.previewID,
        pageUUID: params.pageUUID,
        sectionUUID: params.sectionUUID,
      }),
      type: 'section',
    }
  }

  const isPreviewPageRoute = path === routes.PREVIEW_PAGE_VIEW.template()

  if (!isPreviewPageRoute && path !== routes.PREVIEW_VIEW.template()) {
    return null
  }

  // The root preview route omits the pageUUID - fall back to the current page.
  const pageUUID = isPreviewPageRoute
    ? params.pageUUID
    : preview.currentPage?.uuid

  if (!pageUUID) {
    return null
  }

  // View-mode selection stores the selected entity's id in the frame slot
  // regardless of its kind, so resolve whether it is actually a Section.
  if (selectedUUID) {
    const isSectionSelected = (sections ?? []).some(
      section => section.objectId === selectedUUID
    )

    return isSectionSelected
      ? {
          url: routes.PREVIEW_PAGE_SECTION.create({
            previewID: params.previewID,
            pageUUID,
            sectionUUID: selectedUUID,
          }),
          type: 'section',
        }
      : {
          url: routes.PREVIEW_FRAME_VIEW.create({
            previewID: params.previewID,
            pageUUID,
            frameUUID: selectedUUID,
          }),
          type: 'frame',
        }
  }

  return {
    url: routes.PREVIEW_PAGE_VIEW.create({
      previewID: params.previewID,
      pageUUID,
    }),
    type: 'page',
  }
}
